// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add,
  Refresh,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../config/api';
import QACriteriaDetail from './QACriteriaDetail';
import QACriteriaEdit from './QACriteriaEdit';
import QACriteriaWizard from './QACriteriaWizard';
import ConfirmDialog from './common/ConfirmDialog';
import PageSectionHeader from './common/PageSectionHeader';

/**
 * QACriteriaManagement Component
 * 
 * Main page for QA Criteria Management:
 * - Select criteria from dropdown
 * - View full details
 * - Create new criteria (Phase 4)
 * - Edit existing criteria (Phase 3)
 */
const QACriteriaManagement = () => {
  // State management
  const [criteriaList, setCriteriaList] = useState([]);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState('');
  const [criteriaDetail, setCriteriaDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, data: null });

  // Fetch list of all criteria on mount and when showInactive changes
  useEffect(() => {
    fetchCriteriaList();
  }, [showInactive]);

  // Fetch criteria detail when selection changes
  useEffect(() => {
    if (selectedCriteriaId) {
      fetchCriteriaDetail(selectedCriteriaId);
    } else {
      setCriteriaDetail(null);
    }
  }, [selectedCriteriaId]);

  /**
   * Fetch list of all QA criteria
   */
  const fetchCriteriaList = async () => {
    setLoadingList(true);
    setError(null);
    
    try {
      const response = await apiClient.get(API_ENDPOINTS.QA_CRITERIA_LIST, {
        params: { active_only: !showInactive } // Show all or active only based on checkbox
      });

      if (response.data.success) {
        const list = response.data.criteria || [];
        setCriteriaList(list);
        if (list.length > 0) {
          const defaultCriteria = list.find(c => c.is_default);
          const firstCriteria = defaultCriteria || list[0];
          setSelectedCriteriaId(firstCriteria.id);
        } else {
          setSelectedCriteriaId('');
        }
      } else {
        setError('Failed to load QA criteria list');
      }
    } catch (err) {
      console.error('Error fetching criteria list:', err);
      setError(err.response?.data?.detail || 'Failed to load QA criteria list');
    } finally {
      setLoadingList(false);
    }
  };

  /**
   * Fetch full details of a specific criteria
   */
  const fetchCriteriaDetail = async (criteriaId) => {
    setLoadingDetail(true);
    setError(null);

    try {
      const response = await apiClient.get(`${API_ENDPOINTS.QA_CRITERIA_DETAIL}/${criteriaId}`);

      if (response.data.success) {
        setCriteriaDetail(response.data.criteria);
      } else {
        setError('Failed to load criteria details');
      }
    } catch (err) {
      console.error('Error fetching criteria detail:', err);
      setError(err.response?.data?.detail || 'Failed to load criteria details');
      setCriteriaDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  /**
   * Handle criteria selection change
   */
  const handleCriteriaChange = (event) => {
    setSelectedCriteriaId(event.target.value);
  };

  /**
   * Refresh criteria list
   */
  const handleRefresh = () => {
    fetchCriteriaList();
    if (selectedCriteriaId) {
      fetchCriteriaDetail(selectedCriteriaId);
    }
  };

  /**
   * Handle edit criteria
   */
  const handleEdit = () => {
    setIsEditMode(true);
    setError(null);
    setSuccessMessage(null);
  };

  /**
   * Handle save after edit
   */
  const handleSave = async (updatedCriteria) => {
    setIsEditMode(false);
    setSuccessMessage('QA Criteria updated successfully!');
    
    // Refresh list to show updated name
    await fetchCriteriaList();
    
    // Refresh detail from backend to get latest data (including team relationship)
    if (selectedCriteriaId) {
      await fetchCriteriaDetail(selectedCriteriaId);
    }
  };

  /**
   * Handle cancel edit
   */
  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Refresh detail to discard changes
    if (selectedCriteriaId) {
      fetchCriteriaDetail(selectedCriteriaId);
    }
  };

  /**
   * Handle create new criteria
   */
  const handleCreateNew = () => {
    setIsWizardOpen(true);
    setError(null);
    setSuccessMessage(null);
  };

  /**
   * Handle wizard success
   */
  const handleWizardSuccess = async (newCriteria) => {
    setIsWizardOpen(false);
    setSuccessMessage(`QA Criteria "${newCriteria.name}" created successfully!`);
    // Refresh list first so dropdown has new item with criteria_type, then select it
    await fetchCriteriaList();
    setSelectedCriteriaId(newCriteria.id);
  };

  /**
   * Handle wizard close
   */
  const handleWizardClose = () => {
    setIsWizardOpen(false);
  };

  /**
   * Handle duplicate criteria
   */
  const handleDuplicate = async () => {
    if (!criteriaDetail) return;

    const newName = prompt('Enter name for duplicated criteria:', `${criteriaDetail.name} (Copy)`);
    if (!newName) return;

    try {
      const response = await apiClient.post(
        `${API_ENDPOINTS.QA_CRITERIA_DUPLICATE}/${criteriaDetail.id}/duplicate`,
        {
          new_name: newName,
          new_team_name: criteriaDetail.team_name,
          new_version: criteriaDetail.version
        }
      );

      if (response.data.success) {
        setSuccessMessage(`Criteria duplicated successfully as "${newName}"!`);
        fetchCriteriaList();
        setSelectedCriteriaId(response.data.criteria.id);
      }
    } catch (error) {
      console.error('Error duplicating criteria:', error);
      setError(error.response?.data?.detail || 'Failed to duplicate criteria');
    }
  };

  /**
   * Handle toggle active status
   */
  const handleToggleActive = () => {
    if (!criteriaDetail) return;

    const action = criteriaDetail.is_active ? 'deactivate' : 'activate';
    setConfirmDialog({
      open: true,
      action: 'toggleActive',
      data: { action, criteriaDetail }
    });
  };

  const confirmToggleActive = async () => {
    const { criteriaDetail, action } = confirmDialog.data;
    
    try {
      const response = await apiClient.patch(
        `${API_ENDPOINTS.QA_CRITERIA_ACTIVATE}/${criteriaDetail.id}/activate`,
        { is_active: !criteriaDetail.is_active },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        await fetchCriteriaList();
      }
    } catch (error) {
      console.error('Error toggling active status:', error);
      setError(error.response?.data?.detail || 'Failed to update criteria status');
    }
  };

  /**
   * Handle set as default
   */
  const handleSetDefault = () => {
    if (!criteriaDetail) return;

    setConfirmDialog({
      open: true,
      action: 'setDefault',
      data: { criteriaDetail }
    });
  };

  const confirmSetDefault = async () => {
    const { criteriaDetail } = confirmDialog.data;
    
    try {
      const response = await apiClient.patch(
        `${API_ENDPOINTS.QA_CRITERIA_SET_DEFAULT}/${criteriaDetail.id}/set-default`,
        {},
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        fetchCriteriaList();
        fetchCriteriaDetail(criteriaDetail.id);
      }
    } catch (error) {
      console.error('Error setting default:', error);
      setError(error.response?.data?.detail || 'Failed to set as default');
    }
  };

  /**
   * Handle export to JSON
   */
  const handleExport = () => {
    if (!criteriaDetail) return;

    const exportData = {
      name: criteriaDetail.name,
      team_name: criteriaDetail.team_name,
      department: criteriaDetail.department,
      description: criteriaDetail.description,
      version: criteriaDetail.version,
      knowledge_item_ids: criteriaDetail.knowledge_item_ids ?? null,
      high_level_context: criteriaDetail.high_level_context || null,
      categories: criteriaDetail.categories,
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qa-criteria-${criteriaDetail.team_name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    setSuccessMessage('Criteria exported successfully!');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <PageSectionHeader
          icon={<AssignmentIcon />}
          title="QA Criteria Management"
          description="View, create, and manage quality assessment criteria for voice analysis"
          actions={(
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  color="error"
                />
              )}
              label={(
                <Typography variant="body2" color="text.secondary">
                  Show Inactive
                </Typography>
              )}
            />
          )}
        />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {/* Criteria Selector and Actions */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Criteria Dropdown */}
            <FormControl sx={{ minWidth: 300, flex: 1 }} disabled={loadingList}>
              <InputLabel>Select QA Criteria</InputLabel>
              <Select
                value={selectedCriteriaId}
                onChange={handleCriteriaChange}
                label="Select QA Criteria"
              >
                {loadingList ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading...
                  </MenuItem>
                ) : criteriaList.length === 0 ? (
                  <MenuItem disabled>No criteria available</MenuItem>
                ) : (
                  criteriaList.map((criteria) => (
                    <MenuItem key={criteria.id} value={criteria.id}>
                      {criteria.name}
                      {criteria.criteria_type === 'boolean' ? ' (Yes/No)' : ' (Matrix)'}
                      {criteria.is_default && ' (Default)'}
                      {!criteria.is_active && ' - Inactive'}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Action Buttons */}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loadingList || loadingDetail}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateNew}
              disabled={loadingList}
            >
              Create New
            </Button>
          </Stack>

          {/* Summary Stats */}
          {!loadingList && criteriaList.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={3}>
                <Typography variant="caption" color="text.secondary">
                  Total Criteria: <strong>{criteriaList.length}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active: <strong>{criteriaList.filter(c => c.is_active).length}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Inactive: <strong>{criteriaList.filter(c => !c.is_active).length}</strong>
                </Typography>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Criteria Detail/Edit View */}
        {isEditMode ? (
          <QACriteriaEdit
            criteria={criteriaDetail}
            onSave={handleSave}
            onCancel={handleCancelEdit}
          />
        ) : (
          <QACriteriaDetail 
            criteria={criteriaDetail} 
            loading={loadingDetail}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onToggleActive={handleToggleActive}
            onSetDefault={handleSetDefault}
            onExport={handleExport}
          />
        )}

        {/* Wizard Dialog */}
        <QACriteriaWizard
          open={isWizardOpen}
          onClose={handleWizardClose}
          onSuccess={handleWizardSuccess}
          criteriaList={criteriaList}
        />

        {/* Confirm Dialog */}
        <ConfirmDialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, action: null, data: null })}
          onConfirm={() => {
            if (confirmDialog.action === 'toggleActive') {
              confirmToggleActive();
            } else if (confirmDialog.action === 'setDefault') {
              confirmSetDefault();
            }
          }}
          title={confirmDialog.action === 'toggleActive' ? 'Confirm Status Change' : 'Set as Default'}
          message={
            confirmDialog.action === 'toggleActive'
              ? `Are you sure you want to ${confirmDialog.data?.action} this criteria?`
              : `Set "${confirmDialog.data?.criteriaDetail?.name}" as default for team "${confirmDialog.data?.criteriaDetail?.team_name}"?`
          }
          confirmText="Confirm"
          cancelText="Cancel"
          severity="question"
        />
      </Box>
    </Container>
  );
};

export default QACriteriaManagement;
